import React from "react";
import styled from "@emotion/styled";
import { rem } from "polished";

import { designSystem } from "@/styles/theme";

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';

interface ButtonProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  size?: "small" | "medium" | "large";
  /** 사전 정의된 톤 (variant 사용 시 bgcolor/color/border 자동 적용) */
  variant?: ButtonVariant;
  bgcolor?: string;
  color?: "#6b7280"|"#6b7280"|string;
  buttontype?: "default" | "round";
  border?: boolean | string;
  bold?: string;
  width?: number | string;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  fullwidth?: boolean;
  justifycontents?: string;
  margin?: string;
  display?: string;
  padding?: string;
  textalign?: "left" | "center" | "right";
  fontweight?: string;
  whiteSpace?: "nowrap" | "normal" | "pre" | "pre-wrap" | "pre-line";
  style?: React.CSSProperties;
  title?: string;
  className?: string;
  hover?: {
    bgcolor?: string;
    color?: string;
    borderColor?: string;
    transition?: string;
    opacity?: number;
  };
}

const VARIANT_STYLES: Record<ButtonVariant, { bgcolor: string; color: string; border: boolean }> = {
  primary:   { bgcolor: designSystem.colors.primary, color: '#ffffff', border: false },
  secondary: { bgcolor: '#f1f5f9', color: '#475569', border: false },
  outline:   { bgcolor: '#ffffff', color: designSystem.colors.primary, border: true  },
  danger:    { bgcolor: '#dc3545', color: '#ffffff', border: false },
  ghost:     { bgcolor: 'transparent', color: '#6b7280', border: false },
};

const StyledButton = styled.button<ButtonProps>`
  display: ${(props) => props.display || "flex"};
  align-items: center;
  padding: ${(props) => props.padding || "8px 16px"};
  margin: ${({ margin }) => margin || "0"};
  height: ${(props) => {
    switch (props.size) {
      case "small":
        return "36px";
      case "large":
        return "56px";
      default:
        return "48px";
    }
  }};
  justify-content: ${(props) => props.justifycontents || (props.textalign === "center" ? "center" : props.justifycontents)};
  text-align: ${(props) => props.textalign || "center"};
  width: ${(props) => (props.width ? (typeof props.width === "string" ? props.width : rem(props.width)) : "100%")};
  background-color: ${(props) => props.bgcolor || "lightgray"};
  color: ${(props) => props.color || "#444"};
  border: ${(props) => (props.border ? `1px solid ${props.color}` : "none")};
  cursor: pointer;
  transition: ${(props) => props.hover?.transition || "background-color 0.3s ease"};
  border-radius: ${(props) => (props.buttontype === "round" ? "30px" : "10px")};
  font-weight: ${(props) => props.fontweight || props.bold};
  white-space: ${(props) => props.whiteSpace || "normal"};
  box-sizing: border-box;
  min-width: 0;
  font-size: ${(props) => {
    switch (props.size) {
      case "small":
        return `clamp(${rem(10)}, 1.8vw, ${rem(12)})`;
      case "large":
        return `clamp(${rem(14)}, 2.5vw, ${rem(18)})`;
      default:
        return `clamp(${rem(12)}, 2vw, ${rem(14)})`;
    }
  }};

  @media (max-width: 768px) {
    padding: ${(props) => props.padding || "clamp(5px, 1vw, 8px) clamp(10px, 2vw, 16px)"};
    height: ${(props) => {
      switch (props.size) {
        case "small":
          return "36px";
        case "large":
          return "clamp(44px, 7vw, 56px)";
        default:
          return "clamp(40px, 6vw, 48px)";
      }
    }};
  }

  &:hover {
    ${(props) => props.hover ? `
      ${props.hover.bgcolor ? `background-color: ${props.hover.bgcolor};` : ''}
      ${props.hover.color ? `color: ${props.hover.color};` : ''}
      ${props.hover.borderColor ? `border-color: ${props.hover.borderColor};` : ''}
      ${props.hover.opacity !== undefined ? `opacity: ${props.hover.opacity};` : ''}
    ` : 'opacity: 0.5;'}
  }
`;

const Button = ({
  margin,
  type,
  disabled,
  onClick,
  color,
  bold,
  children,
  icon,
  display,
  size = "medium",
  buttontype = "default",
  textalign,
  fontweight,
  whiteSpace,
  hover,
  variant,
  bgcolor,
  border,
  ...rest
}: ButtonProps) => {
  // variant가 지정되면 색/border 기본값 적용. bgcolor/color/border가 명시되어 있으면 그것이 우선.
  const variantStyle = variant ? VARIANT_STYLES[variant] : undefined;
  const resolvedBgcolor = bgcolor ?? variantStyle?.bgcolor;
  const resolvedColor = color ?? variantStyle?.color;
  const resolvedBorder = border ?? variantStyle?.border;

  return (
    <StyledButton
      display={display}
      type={type}
      margin={margin}
      bold={bold}
      color={resolvedColor}
      bgcolor={resolvedBgcolor}
      border={resolvedBorder}
      onClick={onClick}
      size={size}
      buttontype={buttontype}
      textalign={textalign}
      fontweight={fontweight}
      whiteSpace={whiteSpace}
      hover={hover}
      {...rest}
      disabled={disabled}
    >
      {icon &&
        React.isValidElement(icon) &&
        React.cloneElement(icon, {
          style: { marginRight: "8px" },
        } as React.CSSProperties)}
      {children}
    </StyledButton>
  );
};

export default Button;
